import { apiFetch } from './client';
import type { SlotKind } from './templates';

// ActivityType shares the SlotKind vocabulary so template_slot.kind
// propagates 1:1 into activity.activity_type. Keep these aliased rather
// than duplicated so the SPA fails to compile if the two diverge.
export type ActivityType = SlotKind;
export const ACTIVITY_TYPES: readonly ActivityType[] = [
  'training', 'match', 'meeting', 'cup', 'camp', 'other',
] as const;

// ActivityTeam is the per-team summary the server denormalises onto each
// activity so callers can render team chips (name + colour) without a
// separate listTeams roundtrip. Keeping name + colour here is what makes
// the calendar work for orgs with more teams than fit in any single
// listTeams page.
export interface ActivityTeam {
  id: string;
  name: string;
  color: string;
}

export interface Activity {
  id: string;
  title: string;
  description?: string | null;
  // ActivityType drives the calendar card tint + uppercase eyebrow.
  // One of training / match / meeting / cup / camp / other. Server
  // always emits a value (defaults to 'training' when unset).
  activity_type: ActivityType;
  location_id?: string | null;
  zone_id?: string | null;
  starts_at: string; // RFC3339
  ends_at: string;   // RFC3339
  team_ids: string[];
  // Denormalised {id, name, color} for each linked team. Parallel to
  // team_ids and in the same order. The server populates this from the
  // activity_team join; expect it to be present on every list/get/range
  // response.
  teams: ActivityTeam[];
  template_id?: string | null;
  template_slot_id?: string | null;
  template_week_start?: string | null;
  detached_at?: string | null;
  // When true, the activity is rendered on the org's public website
  // schedule (homepage upcoming-3 block + /c/<slug>/schedule month view).
  // Default false on the server. Toggled per-activity from ActivityFormModal.
  public_visibility: boolean;
  // When true, the activity appears in EVERY org member's personal / home /
  // app schedule regardless of team membership (a club-wide happening — the
  // A-team's game, an annual meeting). Internal visibility only — distinct
  // from public_visibility, which is about the public website. Org-admin-only
  // to set. Default false on the server.
  org_wide: boolean;
  // Per-activity override of the calling lead time: null = inherit the team
  // category; -1 = off; 0 = on create; N = N days before. Present on get()
  // responses; the list/range endpoints may omit it.
  calling_lead_days?: number | null;
}

interface RangeResponse {
  activities: Activity[];
}

interface ListResponse {
  activities: Activity[];
  total: number;
}

const orgPath = (slug: string) =>
  `/api/v1/organizations/${encodeURIComponent(slug)}/activities`;

export interface ListActivitiesOptions {
  team_id?: string;
  location_id?: string;
  zone_id?: string;
  limit?: number;
  offset?: number;
}

export async function listActivities(
  token: string | null,
  slug: string,
  opts: ListActivitiesOptions = {},
): Promise<ListResponse> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(opts)) {
    if (v !== undefined) qs.set(k, String(v));
  }
  const q = qs.toString();
  return apiFetch<ListResponse>(`${orgPath(slug)}${q ? `?${q}` : ''}`, { token });
}

export async function listActivitiesInRange(
  token: string | null,
  slug: string,
  from: Date,
  to: Date,
  filters: { team_id?: string; location_id?: string; zone_id?: string } = {},
): Promise<Activity[]> {
  const qs = new URLSearchParams({
    from: from.toISOString(),
    to: to.toISOString(),
  });
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined) qs.set(k, v);
  }
  const res = await apiFetch<RangeResponse>(`${orgPath(slug)}/range?${qs.toString()}`, { token });
  return res.activities;
}

export async function getActivity(
  token: string | null,
  slug: string,
  id: string,
): Promise<Activity> {
  return apiFetch<Activity>(`${orgPath(slug)}/${encodeURIComponent(id)}`, { token });
}

export interface ActivityInput {
  title: string;
  description?: string;
  activity_type?: ActivityType; // omitted = default 'training' on create
  location_id?: string | null;
  zone_id?: string | null;
  starts_at: string; // ISO 8601
  ends_at: string;
  team_ids: string[];
  // Mirror Activity.public_visibility. Omit on create (server defaults
  // to false); pass explicitly on update to flip the flag from the form.
  public_visibility?: boolean;
  // Mirror Activity.org_wide (club-wide internal visibility). Org-admin-only —
  // the server rejects a non-admin that tries to set/flip it. Omit when the
  // form is rendered for a non-admin.
  org_wide?: boolean;
  // Per-activity calling lead override: null = inherit the team category;
  // -1 = off; 0 = on create; N = N days before. Omit / null to inherit.
  calling_lead_days?: number | null;
}

export async function createActivity(
  token: string | null,
  slug: string,
  input: ActivityInput,
): Promise<Activity> {
  return apiFetch<Activity>(orgPath(slug), { method: 'POST', body: input, token });
}

export async function updateActivity(
  token: string | null,
  slug: string,
  id: string,
  input: ActivityInput,
): Promise<Activity> {
  return apiFetch<Activity>(`${orgPath(slug)}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: input,
    token,
  });
}

export interface RescheduleInput {
  starts_at: string;
  ends_at: string;
  zone_id?: string | null;
}

export async function rescheduleActivity(
  token: string | null,
  slug: string,
  id: string,
  input: RescheduleInput,
): Promise<Activity> {
  return apiFetch<Activity>(`${orgPath(slug)}/${encodeURIComponent(id)}/reschedule`, {
    method: 'POST',
    body: input,
    token,
  });
}

export async function deleteActivity(
  token: string | null,
  slug: string,
  id: string,
  reason = '',
): Promise<void> {
  await apiFetch<void>(`${orgPath(slug)}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    body: { reason },
    token,
  });
}
